'use client';

import { AnchorHTMLAttributes, BlockquoteHTMLAttributes, HTMLAttributes, TdHTMLAttributes } from 'react';
import ReactMarkDown from 'react-markdown';

import { WithLang } from '@/types/general';

export default function MarkdownDescription(props: WithLang<{ description: string }>) {
    return (
        <ReactMarkDown
            className="relative mt-3 flex flex-col overflow-hidden whitespace-pre-wrap text-base leading-7"
            // remarkPlugins={[remarkGfm]}
            components={{
                a: A,
                blockquote: Blockquote,
                code: Code,
                h1: H1,
                h2: H2,
                h3: H3,
                ol: Ol,
                p: P,
                table: Table,
                th: Th,
                td: Td,
                ul: Ul,
                li: Li,
                strong: Strong,
            }}
        >
            {props.description}
        </ReactMarkDown>
    );
}

// custom Markdown Elements
const A = ({ href, children }: AnchorHTMLAttributes<HTMLAnchorElement>) => {
    return (
        <a className="font-semibold leading-3 text-blue-800" href={href} target="_blank" rel="nofollow">
            {children}
        </a>
    );
};

const Ul = ({ children }: HTMLAttributes<HTMLUListElement>) => {
    return <ul className="my-0 list-inside list-disc leading-3">{children}</ul>;
};

const Li = ({ children }: HTMLAttributes<HTMLLIElement>) => {
    return <li className="my-0 list-inside list-disc leading-5">{children}</li>;
};

const Ol = ({ children }: HTMLAttributes<HTMLOListElement>) => {
    return <ol className="my-0 list-inside list-decimal leading-3">{children}</ol>;
};

const Table = ({ children }: HTMLAttributes<HTMLTableElement>) => {
    return <table className="my-0 border border-solid border-black text-left leading-3">{children}</table>;
};

const Th = ({ children }: TdHTMLAttributes<HTMLElement>) => {
    return <th className="my-0 border border-solid border-black p-1">{children}</th>;
};

const Td = ({ children }: TdHTMLAttributes<HTMLElement>) => {
    return <td className="my-0 border border-solid border-black p-1">{children}</td>;
};

const Code = ({ children }: HTMLAttributes<HTMLElement>) => {
    return <code className="my-0 rounded-md border-black bg-black bg-opacity-20 p-1">{children}</code>;
};

const Blockquote = ({ children }: BlockquoteHTMLAttributes<HTMLElement>) => {
    return (
        <blockquote className="border-l-solid my-0 border-l border-l-gray-400 py-0 pl-4 leading-3">
            {children}
        </blockquote>
    );
};

const H1 = ({ children }: HTMLAttributes<HTMLHeadElement>) => {
    return <h2 className="my-8 block text-3xl font-bold leading-7">{children}</h2>;
};

const H2 = ({ children }: HTMLAttributes<HTMLHeadElement>) => {
    return <h3 className="my-4 block text-2xl font-bold leading-7">{children}</h3>;
};

const H3 = ({ children }: HTMLAttributes<HTMLHeadElement>) => {
    return <h3 className="my-2 text-xl font-bold leading-5">{children}</h3>;
};

const P = ({ children }: HTMLAttributes<HTMLParagraphElement>) => {
    return <p className="my-0 py-0 leading-5">{children}</p>;
};

const Strong = ({ children }: HTMLAttributes<HTMLElement>) => {
    return <strong className="my-0 py-0 leading-[28px]">{children}</strong>;
};
